/* ==========================================================================
   FACETS
   Filtering, sorting and paginating a collection without a full page load,
   using Shopify's Search & Discovery filters plus the Section Rendering API.

   The markup is plain forms with real inputs and real links, so the page still
   filters correctly with JavaScript disabled — this only removes the reload.

   Every listener is delegated from the <facet-filters> element rather than
   bound to a form, because the sidebar and drawer forms are both replaced
   wholesale on each update; a listener bound to a form would be thrown away
   with it after the first change.
   ========================================================================== */

(function () {
  'use strict';

  const Theme = window.Theme;

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.controller = null;

      this.schedule = Theme.debounce((form) => this.apply(form), 320);

      this.addEventListener('input', (event) => {
        const form = event.target.closest('[data-facet-form]');
        if (!form) return;
        // Price is typed, so it waits for `change` rather than firing on each
        // keystroke and cancelling its own request.
        if (event.target.matches('input[type="number"]')) return;
        this.schedule(form);
      });

      this.addEventListener('change', (event) => {
        const form = event.target.closest('[data-facet-form]');
        if (!form) return;
        if (event.target.matches('input[type="number"]')) this.schedule(form);
      });

      this.addEventListener('submit', (event) => {
        const form = event.target.closest('[data-facet-form]');
        if (!form) return;
        event.preventDefault();
        this.apply(form);
      });

      // Active-filter pills, "clear all" and pagination are ordinary links.
      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-facet-link]');
        if (!link) return;
        event.preventDefault();
        this.navigate(link.href);
      });

      this.onPopState = () => this.navigate(window.location.href, false);
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      if (this.controller) this.controller.abort();
    }

    buildUrl(form) {
      const params = new URLSearchParams(new FormData(form));

      // Empty price bounds should not appear in the URL at all. Keys are
      // collected first — deleting while iterating a URLSearchParams skips
      // entries.
      const empty = [];
      params.forEach((value, key) => {
        if (value === '') empty.push(key);
      });
      empty.forEach((key) => params.delete(key));

      // A filter change always returns to the first page.
      params.delete('page');

      const query = params.toString();
      return window.location.pathname + (query ? '?' + query : '');
    }

    apply(form) {
      this.navigate(this.buildUrl(form));
    }

    navigate(url, push = true) {
      const targets = document.querySelectorAll('[data-facet-target]');
      targets.forEach((target) => target.classList.add('is-loading'));

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const requestUrl = new URL(url, window.location.origin);
      requestUrl.searchParams.set('section_id', this.sectionId);

      fetch(requestUrl.toString(), { signal: this.controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error('Could not load results');
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');

          // Swap every region the filters own: the grid, the counts, the
          // sidebar, the drawer facets and the active-filter pills.
          document.querySelectorAll('[data-facet-region]').forEach((region) => {
            const fresh = doc.querySelector(
              '[data-facet-region="' + region.dataset.facetRegion + '"]'
            );
            if (fresh) region.innerHTML = fresh.innerHTML;
          });

          if (push) {
            const clean = new URL(url, window.location.origin);
            clean.searchParams.delete('section_id');
            window.history.pushState({ facets: true }, '', clean.toString());
          }

          targets.forEach((target) => {
            target.classList.remove('is-loading');
            Theme.observeReveals(target);
          });

          this.announce();
          this.restoreScroll();
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          // A failed partial render should never leave the shopper stranded.
          window.location.href = url;
        });
    }

    announce() {
      const region = document.querySelector('[data-facet-status]');
      const count = document.querySelector('[data-facet-count]');
      if (region && count) region.textContent = count.textContent.trim();
    }

    /* Bring the top of the grid back into view, but only if it scrolled away. */
    restoreScroll() {
      const anchor = document.querySelector('[data-facet-scroll-anchor]');
      if (!anchor) return;

      const top = anchor.getBoundingClientRect().top + window.scrollY;
      const offset =
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--sticky-offset')
        ) || 64;

      if (window.scrollY > top - offset) {
        window.scrollTo({ top: top - offset - 8, behavior: 'smooth' });
      }
    }
  }

  customElements.define('facet-filters', FacetFilters);

  /* ------------------------------------------------------------------------
     <sort-by> — hands the new sort order to the same navigation path.
     ------------------------------------------------------------------------ */

  class SortBy extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        const select = event.target.closest('select');
        if (!select) return;

        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');

        const filters = document.querySelector('facet-filters');
        if (filters && typeof filters.navigate === 'function') filters.navigate(url.toString());
        else window.location.href = url.toString();
      });
    }
  }

  customElements.define('sort-by', SortBy);

})();
